import { create } from 'zustand';
import { loginUser, registerUser, fetchUserProfile, updateProfile as apiUpdateProfile, uploadAvatar as apiUploadAvatar, completeOnboarding as apiCompleteOnboarding } from '../api/auth';
import { getAccessToken, setAccessToken } from '../api/client';
import apiClient from '../api/client';
import type { UserProfile } from '../types/auth';

interface User {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  staff_role?: 'owner' | 'admin' | 'editor' | 'support' | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  onboarding_completed: boolean;
  self_assessed_level: string | null;
  email_verified?: boolean;
  consent_given_at?: string | null;
  plan?: 'free' | 'premium_monthly' | 'premium_yearly';
  subscription_status?: string;
  exam_date: string | null;
  exam_center: string | null;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, consent: boolean, captchaToken?: string, acceptTerms?: boolean, termsVersion?: string) => Promise<string>;
  logout: () => Promise<void>;
  setAuth: (token: string, user: User) => void;
  loadFromStorage: () => Promise<void>;
  updateProfile: (payload: {
    display_name?: string | null;
    username: string;
    email: string;
    bio?: string | null;
    exam_date?: string | null;
    exam_center?: string | null;
  }) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  completeOnboarding: (payload: { display_name?: string; self_assessed_level?: string }) => Promise<void>;
}

function profileFromResponse(data: UserProfile): User {
  return {
    id: data.id,
    username: data.username,
    email: data.email,
    is_admin: data.is_admin ?? false,
    staff_role: data.staff_role ?? null,
    display_name: data.display_name ?? null,
    bio: data.bio ?? null,
    avatar_url: data.avatar_url ?? null,
    onboarding_completed: data.onboarding_completed ?? false,
    self_assessed_level: data.self_assessed_level ?? null,
    consent_given_at: data.consent_given_at ?? null,
    plan: data.plan ?? undefined,
    subscription_status: data.subscription_status ?? undefined,
    exam_date: data.exam_date ?? null,
    exam_center: data.exam_center ?? null,
  };
}

export const useAuthStore = create<AuthState>((set, _get) => ({
  token: getAccessToken(),
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    try {
      const data = await loginUser(email, password);
      // Store access token in memory only (NOT localStorage)
      setAccessToken(data.access_token);
      set({ token: data.access_token, user: profileFromResponse(data.user), isAuthenticated: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
      if (axiosErr.response?.status === 403 && axiosErr.response?.data?.detail?.toLowerCase().includes('not verified')) {
        throw { emailNotVerified: true, email };
      }
      throw err;
    }
  },

  register: async (username, email, password, consent, captchaToken, acceptTerms, termsVersion) => {
    await registerUser(username, email, password, consent, captchaToken || '', acceptTerms ?? false, termsVersion ?? '2026-03');
    // Don't set token -- user must verify email first
    return email;
  },

  logout: async () => {
    try {
      // Revoke refresh token on server (clears httpOnly cookie)
      await apiClient.post('/api/auth/revoke');
    } catch {
      // Best-effort: clear local state even if revoke fails
    }
    setAccessToken(null);
    // Clean up any legacy localStorage token
    localStorage.removeItem('cloudmaster_token');
    set({ token: null, user: null, isAuthenticated: false });
  },

  setAuth: (token, user) => {
    setAccessToken(token);
    set({ token, user: profileFromResponse(user as UserProfile), isAuthenticated: true, isLoading: false });
  },

  loadFromStorage: async () => {
    // Migration: check localStorage for legacy token and move to memory
    const legacyToken = localStorage.getItem('cloudmaster_token');
    if (legacyToken) {
      setAccessToken(legacyToken);
      localStorage.removeItem('cloudmaster_token');
    }

    // Try to restore session via refresh token cookie
    const currentToken = getAccessToken();
    if (!currentToken) {
      // No in-memory token — try silent refresh via httpOnly cookie
      try {
        const resp = await apiClient.post('/api/auth/refresh');
        const newToken = resp.data?.access_token;
        if (newToken) {
          setAccessToken(newToken);
          const data = await fetchUserProfile();
          set({ token: newToken, user: profileFromResponse(data), isAuthenticated: true, isLoading: false });
          return;
        }
      } catch {
        // No valid refresh token — user is logged out
      }
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const data = await fetchUserProfile();
      set({ token: currentToken, user: profileFromResponse(data), isAuthenticated: true, isLoading: false });
    } catch {
      setAccessToken(null);
      set({ token: null, user: null, isAuthenticated: false, isLoading: false });
    }
  },

  updateProfile: async (payload) => {
    const updated = await apiUpdateProfile(payload);
    set((s) => ({ user: s.user ? profileFromResponse(updated) : null }));
  },

  uploadAvatar: async (file) => {
    const updated = await apiUploadAvatar(file);
    set((s) => ({ user: s.user ? profileFromResponse(updated) : null }));
  },

  completeOnboarding: async (payload) => {
    const updated = await apiCompleteOnboarding({
      ...payload,
      onboarding_completed: true,
    });
    set((s) => ({ user: s.user ? profileFromResponse(updated) : null }));
  },
}));
